/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import axios from "@/api/axios";
import { useSession } from "@/hooks/useSession";
import {
  Box,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
  Paper,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";

import Grid from "@mui/material/Grid2";
import CloseIcon from "@mui/icons-material/Close";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import FileUploadField from "@/components/FileUploadField";
import { useTranslation } from "react-i18next";

const LoadAvatar = ({ user, avatarUrl, onClose, setToast, onAvatarUpload }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { token } = useSession();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileNames, setFileNames] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  // Cleanup preview URL when modal closes
  const handleClose = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setSelectedFile(null);
    setFileNames([]);
    onClose();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const file = e.target.files[0];

    // Clean up previous preview URL
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setSelectedFile(file);
    setFileNames(files.map((file) => file.name));

    // Create preview URL for the new file
    if (file && file.type.startsWith("image/")) {
      const newPreviewUrl = URL.createObjectURL(file);
      setPreviewUrl(newPreviewUrl);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    const formData = new FormData();
    formData.append("avatar", selectedFile);

    try {
      const response = await axios.post(
        `/api/v1/users/${user.id}/avatar`,
        formData,
        {
          headers: {
            /*  "Content-Type": "multipart/form-data", */
            Authorization: `Bearer ${token}`,
          },
        }
      );
      onAvatarUpload?.(response.data);

      setToast({
        open: true,
        severity: "success",
        message: t("loadAvatar.successToast"),
      });
      handleClose();
    } catch (error) {
      const msg = error.response?.data?.message || error.message;
      setToast({
        open: true,
        severity: "error",
        message: t("loadAvatar.errorToast", { error: msg }),
      });
    }
  };

  return (
    <Dialog
      open
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      sx={{
        "& .MuiDialog-paper": {
          borderRadius: 3,
          boxShadow: theme.shadows[24],
          border: "1px solid",
          borderColor: "divider",
          background: theme.palette.background.paper,
          overflow: "hidden",
        },
      }}
      aria-labelledby="load-avatar-dialog-title"
    >
      <DialogTitle
        id="load-avatar-dialog-title"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 3,
          px: 4,
          borderBottom: "1px solid",
          borderColor: "divider",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.primary.main, 0.02)} 100%)`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PhotoCameraIcon color="primary" sx={{ fontSize: 28 }} />
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "text.primary",
              }}
            >
              {t("loadAvatar.title")}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                mt: 0.5,
              }}
            >
              {t("loadAvatar.updateProfileSubtitle")}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={handleClose}
          sx={{
            bgcolor: alpha(theme.palette.text.primary, 0.08),
            "&:hover": {
              bgcolor: alpha(theme.palette.text.primary, 0.12),
              transform: "scale(1.05)",
            },
            transition: "all 0.2s ease",
          }}
          aria-label={t("loadAvatar.closeDialogAria")}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Current Avatar Section */}
          <Grid size={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                textAlign: "center",
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.8)} 0%, ${alpha(theme.palette.grey[100], 0.4)} 100%)`,
                ...(theme.palette.mode === "dark" && {
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[800], 0.8)} 0%, ${alpha(theme.palette.grey[900], 0.4)} 100%)`,
                }),
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {t("loadAvatar.currentAvatarSection")}
              </Typography>
              <Avatar
                src={avatarUrl || ""}
                alt={t("loadAvatar.avatarAlt")}
                sx={{
                  width: 120,
                  height: 120,
                  margin: "0 auto",
                  border: "4px solid",
                  borderColor: "background.paper",
                  boxShadow: theme.shadows[8],
                  fontSize: "2.5rem",
                  fontWeight: 600,
                }}
                aria-label={t("loadAvatar.avatarAria")}
                onError={(e) => {
                  e.target.src = "";
                }}
              >
                {!avatarUrl &&
                  (user?.first_name?.[0] || user?.username?.[0] || "U")}
              </Avatar>

              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mt: 2,
                  fontStyle: "italic",
                  textAlign: "center",
                }}
              >
                {avatarUrl
                  ? t("loadAvatar.profilePictureSet")
                  : t("loadAvatar.profilePictureNotSet")}
              </Typography>
            </Paper>
          </Grid>

          {/* File Upload Section */}
          <Grid size={12}>
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                background: alpha(theme.palette.primary.main, 0.02),
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  color: "text.secondary",
                  mb: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                }}
              >
                {t("loadAvatar.chooseNewAvatarSection")}
              </Typography>
              <FileUploadField
                label={t("loadAvatar.fileUploadLabel")}
                fileNames={fileNames}
                onChange={handleFileChange}
                multiple={false}
                accept={"image"}
              />

              {selectedFile && (
                <Box
                  sx={{
                    mt: 3,
                    p: 3,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.success.main, 0.08),
                    border: "1px solid",
                    borderColor: alpha(theme.palette.success.main, 0.2),
                  }}
                >
                  <Typography
                    variant="subtitle2"
                    sx={{
                      color: "success.dark",
                      fontWeight: 600,
                      mb: 2,
                      textAlign: "center",
                    }}
                  >
                    {t("loadAvatar.newAvatarPreview")}
                  </Typography>

                  {previewUrl && (
                    <Avatar
                      src={previewUrl}
                      sx={{
                        width: 80,
                        height: 80,
                        margin: "0 auto 16px auto",
                        border: "3px solid",
                        borderColor: "success.main",
                        boxShadow: theme.shadows[4],
                      }}
                    />
                  )}

                  <Typography
                    variant="body2"
                    sx={{
                      color: "success.dark",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      justifyContent: "center",
                      mb: 1,
                    }}
                  >
                    <PhotoCameraIcon sx={{ fontSize: 16 }} />
                    {selectedFile.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      display: "block",
                      textAlign: "center",
                    }}
                  >
                    {t("loadAvatar.fileSizeInfo", {
                      size: (selectedFile.size / 1024 / 1024).toFixed(2),
                    })}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 4,
          pt: 0,
          gap: 2,
          justifyContent: "flex-end",
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            borderRadius: 2,
            px: 3,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          {t("loadAvatar.cancelButton")}
        </Button>
        <Button
          disabled={!selectedFile}
          variant="contained"
          onClick={handleUpload}
          sx={{
            borderRadius: 2,
            px: 4,
            py: 1.5,
            textTransform: "none",
            fontWeight: 600,
            boxShadow: theme.shadows[2],
            "&:hover": {
              boxShadow: theme.shadows[4],
            },
            "&.Mui-disabled": {
              bgcolor: alpha(theme.palette.action.disabled, 0.12),
            },
          }}
          aria-label={t("loadAvatar.uploadButtonAria")}
        >
          {t("loadAvatar.uploadButton")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LoadAvatar;
